/**
 * Placeholder module screen.
 *
 * A reusable, intentional-looking "coming next" screen for the console modules
 * that are not yet built (Direct Booking, Channel Manager, Reconciliation,
 * Revenue & Rates, Guest CRM, Automation, Marketing & SEO, Operations,
 * Analytics, Admin & Settings). Shows the module title, its one-line SRS
 * description, and a clear "Module in development" note so it reads as planned
 * rather than broken.
 *
 * Pure and SSR-safe (a function of its props only).
 */
import type { ReactElement } from "react";

export interface PlaceholderScreenProps {
  /** The module title (e.g. "Channel Manager"). */
  readonly title: string;
  /** One-line SRS description of the module. */
  readonly description: string;
}

export default function PlaceholderScreen({
  title,
  description,
}: PlaceholderScreenProps): ReactElement {
  return (
    <section
      aria-labelledby="placeholder-heading"
      data-console-screen="placeholder"
    >
      <h1 id="placeholder-heading">{title}</h1>
      <p className="console-lede">{description}</p>

      <div className="console-placeholder">
        <span className="console-placeholder__badge">In development</span>
        <h2 className="console-placeholder__title">Module in development</h2>
        <p className="console-placeholder__body">
          This module is on the AVOS roadmap and will land in an upcoming
          release. The navigation, layout, and data model are already in place —
          the screen for {title} is being built next.
        </p>
      </div>
    </section>
  );
}
